// ======================================================
// Updated by: Halimunnisa
// Feature: Customer Acceptance Workflow
// Purpose: Applied active queue filtering to Initial Quotation Approval screen.
// ======================================================
import React, { useState, useMemo } from "react";
import { Search } from "lucide-react";
import { DataTable, Avatar, Badge } from "@/components/ui";
import { TABLE_BACKGROUNDS } from "@/constants/tableBackgrounds";
import ConfirmationModal from "../modals/ConfirmationModal";
import InitialQuotationApprovalControlPanel from "../components/InitialQuotationApprovalControlPanel";
import api from "../../../../services/api";
import { showApiError, showSuccessMessage } from "@/lib/toast";
import { useAuthStore } from "@/store/authStore";
import { Eye, Check, X, CheckCircle2 } from "lucide-react";
import LeadDetailsViewModal from "@/components/modals/LeadDetailsViewModal";

const RoleSelectionScreen = ({ leads, isLoading, onRefresh, teamMembers = [], isViewOnly = false }) => {
  const { user } = useAuthStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [viewModalLead, setViewModalLead] = useState(null);
  const [actionType, setActionType] = useState('approve');
  const [actionLoading, setActionLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [localSearch, setLocalSearch] = useState("");
  const [localFilters, setLocalFilters] = useState({
    date: "all",
    fromDate: "",
    toDate: "",
  });

  const filteredLeads = useMemo(() => {
    console.log("RoleSelectionScreen received leads:", leads);
    if (!leads) return [];

    // ======================================================
    // Updated by: Halimunnisa
    // Active queue filtering added
    // Prevents cumulative stage visibility
    // ======================================================
    let result = leads.filter(
      (l) =>
        l.leadStatus === "customer_acceptance" ||
        l.status === "customer_acceptance" ||
        (l.initialQuotationApproval && l.initialQuotationApproval.isRequested === true)
    );

    const userRole = (user?.role || "").toLowerCase();
    const isDesignerUser = userRole.includes("designer") || userRole === "design";
    const isPmUser = userRole.includes("project manager") || userRole.includes("project-manager") || userRole.includes("pm") || userRole === "manager";

    if (isDesignerUser) {
      result = result.filter(l => l.initialQuotationApproval?.designerUser?.toString() === user._id?.toString());
    } else if (isPmUser) {
      // Only show to PM if designer has approved or skipped
      result = result.filter(l => 
        l.initialQuotationApproval?.managerUser?.toString() === user._id?.toString() && 
        (l.initialQuotationApproval?.designer?.status === 'approved' || l.initialQuotationApproval?.designer?.status === 'skipped')
      );
    }

    console.log("RoleSelectionScreen result after status filter:", result);

    if (localSearch) {
      const lowerSearch = localSearch.toLowerCase();
      result = result.filter(
        (l) =>
          l.customId?.toLowerCase().includes(lowerSearch) ||
          l.customerName?.toLowerCase().includes(lowerSearch) ||
          l.firstName?.toLowerCase().includes(lowerSearch) ||
          l.email?.toLowerCase().includes(lowerSearch),
      );
    }

    const { date, fromDate, toDate } = localFilters;
    if (date !== "all" || fromDate || toDate) {
      let startDate = null;
      let endDate = null;

      const now = new Date();
      if (date && date !== "all") {
        startDate = new Date();
        switch (date) {
          case "today":
            startDate.setHours(0, 0, 0, 0);
            break;
          case "this_week":
          case "week":
            startDate.setDate(now.getDate() - 7);
            break;
          case "this_month":
          case "month":
            startDate.setDate(now.getDate() - 30);
            break;
          case "this_year":
          case "year":
            startDate.setDate(now.getDate() - 365);
            break;
        }
        endDate = now;
      } else {
        if (fromDate) startDate = new Date(fromDate);
        if (toDate) endDate = new Date(toDate);
      }

      if (startDate || endDate) {
        result = result.filter((l) => {
          const createdAt = new Date(l.createdAt);
          if (startDate && createdAt < startDate) return false;
          if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            if (createdAt > end) return false;
          }
          return true;
        });
      }
    }

    return result;
  }, [leads, localSearch, localFilters]);

  const handleRoleSelect = (role, lead, type = 'approve') => {
    setSelectedRole(role);
    setSelectedLead(lead);
    setActionType(type);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLead(null);
  };

  const handleConfirmRole = async ({ status, notes }) => {
    if (!selectedLead || !selectedRole) return;

    setActionLoading(true);
    try {
      const roleKey = selectedRole.toLowerCase() === 'project manager' ? 'manager' : selectedRole.toLowerCase();
      
      const updateData = {
        [roleKey]: {
          status,
          notes
        }
      };

      await api.post(`/verve-contacts/${selectedLead._id}/initial-quotation-approval`, updateData);

      showSuccessMessage(
        `${selectedRole} ${status === "approved" ? "Approved" : "Rejected"} successfully`,
      );
      setIsModalOpen(false);
      setSelectedLead(null);

      if (onRefresh) {
        onRefresh();
      } else {
        window.location.reload();
      }
    } catch (err) {
      showApiError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const getApprovalStatus = (approval) => {
    if (!approval || !approval.isRequested)
      return { text: "Not Requested", color: "text-slate-400 bg-slate-100" };

    const { designer, manager, owner, approvalStatus } = approval;
    
    if (approvalStatus === "fully_approved" || owner?.status === "approved")
      return { text: "Fully Approved", color: "text-green-700 bg-green-100" };

    if (owner?.status === "rejected")
      return { text: "Rejected by Owner", color: "text-red-700 bg-red-100" };
    if (manager?.status === "rejected")
      return { text: "Rejected by Manager", color: "text-red-700 bg-red-100" };
    if (designer?.status === "rejected")
      return { text: "Rejected by Designer", color: "text-red-700 bg-red-100" };

    const userRole = (user?.role || "").toLowerCase();
    const isDesignerUser = userRole.includes("designer") || userRole === "design";
    const isPmUser = userRole.includes("project manager") || userRole.includes("project-manager") || userRole.includes("pm") || userRole === "manager";

    if (isDesignerUser && designer?.status === "approved") {
      return { text: "Accepted", color: "text-green-700 bg-green-100" };
    }
    
    if (isPmUser && manager?.status === "approved") {
      return { text: "Accepted", color: "text-green-700 bg-green-100" };
    }

    if (designer?.status === "pending")
      return { text: "Pending Designer", color: "text-blue-700 bg-blue-100" };

    if (manager?.status === "pending")
      return { text: "Pending Manager", color: "text-blue-700 bg-blue-100" };

    if (owner?.status === "pending")
      return { text: "Pending Owner", color: "text-blue-700 bg-blue-100" };

    return { text: "Requested", color: "text-blue-700 bg-blue-100" };
  };

  const getUserName = (id) => {
    if (!id) return "Unassigned";
    const user = teamMembers.find(u => u._id?.toString() === id.toString());
    return user ? user.name : "Unknown User";
  };

  const handleExport = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      const { exportToExcel } = await import('@/utils/exportUtils');
      const { leadService } = await import('@/pages/verve-leads/leads/services/leadService');
      const { getCumulativeStatuses } = await import('@/constants/globalConstants');
      
      const params = {
        limit: 10000,
        status: getCumulativeStatuses('customer_acceptance')
      };
      
      const response = await leadService.getLeads(params);
      const rawLeads = Array.isArray(response?.data) ? response.data : (response?.data?.leads || []);

      let result = rawLeads.filter(
        (l) =>
          l.leadStatus === "customer_acceptance" ||
          l.status === "customer_acceptance" ||
          (l.initialQuotationApproval && l.initialQuotationApproval.isRequested === true)
      );

      const userRole = (user?.role || "").toLowerCase();
      const isDesignerUser = userRole.includes("designer") || userRole === "design";
      const isPmUser = userRole.includes("project manager") || userRole.includes("project-manager") || userRole.includes("pm") || userRole === "manager";

      if (isDesignerUser) {
        result = result.filter(l => l.initialQuotationApproval?.designerUser?.toString() === user._id?.toString());
      } else if (isPmUser) {
        result = result.filter(l => 
          l.initialQuotationApproval?.managerUser?.toString() === user._id?.toString() && 
          (l.initialQuotationApproval?.designer?.status === 'approved' || l.initialQuotationApproval?.designer?.status === 'skipped')
        );
      }

      if (localSearch) {
        const lowerSearch = localSearch.toLowerCase();
        result = result.filter(
          (l) =>
            l.customId?.toLowerCase().includes(lowerSearch) ||
            l.customerName?.toLowerCase().includes(lowerSearch) ||
            l.firstName?.toLowerCase().includes(lowerSearch) ||
            l.email?.toLowerCase().includes(lowerSearch),
        );
      }

      const { date, fromDate, toDate } = localFilters;
      if (date !== "all" || fromDate || toDate) {
        let startDate = null;
        let endDate = null;

        const now = new Date();
        if (date && date !== "all") {
          startDate = new Date();
          switch (date) {
            case "today": startDate.setHours(0, 0, 0, 0); break;
            case "this_week": case "week": startDate.setDate(now.getDate() - 7); break;
            case "this_month": case "month": startDate.setDate(now.getDate() - 30); break;
            case "this_year": case "year": startDate.setDate(now.getDate() - 365); break;
          }
          endDate = now;
        } else {
          if (fromDate) startDate = new Date(fromDate);
          if (toDate) endDate = new Date(toDate);
        }

        if (startDate || endDate) {
          result = result.filter((l) => {
            const createdAt = new Date(l.createdAt);
            if (startDate && createdAt < startDate) return false;
            if (endDate) {
              const end = new Date(endDate);
              end.setHours(23, 59, 59, 999);
              if (createdAt > end) return false;
            }
            return true;
          });
        }
      }

      if (result.length === 0) {
        showApiError(new Error("No data to export"));
        return;
      }

      const exportData = result.map(l => {
        const approval = l.initialQuotationApproval || {};
        const fullName = l.customerName || `${l.firstName || ""} ${l.lastName || ""}`.trim() || "No Name";
        const displayNote = approval?.requestNotes || l.stageNotes?.customer_acceptance || l.lastNote || l.notes || "—";
        const statusObj = getApprovalStatus(approval);
        
        return {
          "Verve ID": l.customId || "—",
          "Contact Name": fullName,
          "Phone": l.phoneNumber || l.phone || "—",
          "Email": l.email || "—",
          "Location": l.city || l.location || "—",
          "Notes": displayNote,
          "Status": statusObj.text,
          "Design Approval": approval.designer?.status || "—",
          "PM Approval": approval.manager?.status || "—",
          "Owner Approval": approval.owner?.status || "—"
        };
      });

      const pad = n => String(n).padStart(2, '0');
      const nowDt = new Date();
      const dtStr = `${nowDt.getFullYear()}-${pad(nowDt.getMonth()+1)}-${pad(nowDt.getDate())}`;

      await exportToExcel(exportData, `Initial_Quotation_Approvals_${dtStr}`);
      showSuccessMessage("Export completed successfully");
    } catch (err) {
      console.error('Export failed:', err);
      showApiError(new Error('Failed to export approvals'));
    } finally {
      setIsExporting(false);
    }
  };

  const renderApprovalInfo = (roleName, roleData, userId) => {
    if (!roleData) return null;
    return (
      <div className="flex flex-col">
        <span className="text-[11px] font-bold text-slate-700 truncate">
          {getUserName(userId)}
        </span>
        <span className={`w-fit mt-1 text-[9px] px-1.5 py-0.5 rounded font-bold uppercase ${
          roleData.status === 'approved' ? 'bg-green-100 text-green-700' :
          roleData.status === 'rejected' ? 'bg-red-100 text-red-700' :
          roleData.status === 'skipped' ? 'bg-slate-100 text-slate-500' :
          'bg-yellow-100 text-yellow-700'
        }`}>
          {roleData.status}
        </span>
        {roleData.notes && (
          <div className="text-[10px] text-slate-500 mt-1 italic line-clamp-2">
            "{roleData.notes}"
          </div>
        )}
      </div>
    );
  };

  const columns = useMemo(() => {
    const cols = [
      {
        header: "Verve ID",
        key: "customId",
        minWidth: "170px",
        render: (l) => (
          <span className="text-[10px] uppercase tracking-tight whitespace-nowrap">
            {l.customId || "—"}
          </span>
        ),
      },
      {
        header: "Contact Details",
        key: "customerName",
        render: (l) => {
          const fullName =
            l.customerName ||
            `${l.firstName || ""} ${l.lastName || ""}`.trim() ||
            "No Name";
          return (
            <div className="flex flex-col min-w-0 py-1">
              <span className="text-brand-dark tracking-tight text-[13px] font-medium leading-tight truncate">
                {fullName}
              </span>
              <span className="text-[11px] text-slate-500 truncate flex items-center mt-0.5 tracking-tighter">
                {l.phoneNumber || l.phone || "No Phone"}
                {l.email && <span className="mx-1.5 opacity-40">|</span>}
                {l.email && l.email.toLowerCase()}
              </span>
            </div>
          );
        },
      },
      {
        header: "Location",
        key: "location",
        render: (l) => (
          <span className="text-[11px] text-slate-600 font-medium">
            {l.city || l.location || "N/A"}
          </span>
        ),
      },
      {
        header: "Notes",
        key: "notes",
        minWidth: "160px",
        render: (l) => {
          const approval = l.initialQuotationApproval;
          const displayNote = approval?.requestNotes || l.stageNotes?.customer_acceptance || l.lastNote || l.notes;
          
          if (!displayNote) {
            return <span className="text-[11px] text-slate-400 italic">—</span>;
          }
          return (
            <div className="text-[11px] text-slate-600 italic whitespace-normal">
              "{displayNote}"
            </div>
          );
        },
      },
    ];

    if (isViewOnly) {
      cols.push(
        {
          header: "Design Approval",
          key: "designApproval",
          minWidth: "160px",
          render: (l) => {
            const approval = l.initialQuotationApproval;
            if (!approval || !approval.isRequested) {
              return <span className="text-[11px] text-slate-400 italic">Not Assigned</span>;
            }
            return <div className="w-full">{renderApprovalInfo("Design", approval.designer, approval.designerUser)}</div>;
          },
        },
        {
          header: "PM Approval",
          key: "pmApproval",
          minWidth: "160px",
          render: (l) => {
            const approval = l.initialQuotationApproval;
            if (!approval || !approval.isRequested) {
              return <span className="text-[11px] text-slate-400 italic">Not Assigned</span>;
            }
            return <div className="w-full">{renderApprovalInfo("PM", approval.manager, approval.managerUser)}</div>;
          },
        },
        {
          header: "Owner Approval",
          key: "ownerApproval",
          minWidth: "160px",
          render: (l) => {
            const approval = l.initialQuotationApproval;
            if (!approval || !approval.isRequested) {
              return <span className="text-[11px] text-slate-400 italic">Not Assigned</span>;
            }
            return <div className="w-full">{renderApprovalInfo("Owner", approval.owner, approval.ownerUser)}</div>;
          },
        }
      );
    }

    cols.push(      {
        header: "Status",
        key: "status",
        render: (l) => {
          const status = getApprovalStatus(l.initialQuotationApproval);
          return (
            <span
              className={`text-[10px] px-2 py-1 rounded-md font-bold uppercase tracking-wider whitespace-nowrap inline-block ${status.color}`}
            >
              {status.text}
            </span>
          );
        },
      },
      {
        header: "Actions",
        key: "actions",
        render: (l) => {
          const approval = l.initialQuotationApproval || {};
          const { designer, manager, owner, isRequested, approvalStatus } = approval;

          const ViewButton = (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setViewModalLead(l);
              }}
              className="text-slate-500 hover:text-indigo-600 transition-colors"
              title="View Details"
            >
              <Eye size={18} />
            </button>
          );

          if (!isRequested) {
            return (
              <div className="flex items-center gap-2">
                {ViewButton}
                <span className="text-xs text-slate-400 italic">
                  No request sent
                </span>
              </div>
            );
          }

          const isRejected =
            designer?.status === "rejected" ||
            manager?.status === "rejected" ||
            owner?.status === "rejected";
          const isFullyApproved = approvalStatus === "fully_approved" || owner?.status === "approved";

          if (isFullyApproved) {
            return (
              <div className="flex items-center gap-2">
                {ViewButton}
                <CheckCircle2 size={20} className="text-emerald-500" title="Fully Approved" />
              </div>
            );
          }

          if (isRejected) {
            return (
              <div className="flex items-center gap-2">
                {ViewButton}
                <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">
                  Approval Rejected
                </span>
              </div>
            );
          }

          const userRole = (user?.role || "").toLowerCase();
          const isDesignerUser = userRole.includes("designer") || userRole === "design";
          const isPmUser = userRole.includes("project manager") || userRole.includes("project-manager") || userRole.includes("pm") || userRole === "manager";
          const isOwnerUser = userRole.includes("owner");

          const showDesigner = !isViewOnly && designer?.status === "pending" && isDesignerUser;
          const showManager = !isViewOnly && (designer?.status === "approved" || designer?.status === "skipped") && manager?.status === "pending" && isPmUser;
          const showOwner = !isViewOnly && (manager?.status === "approved" || manager?.status === "skipped") && owner?.status === "pending" && isOwnerUser;

          const hasDesignerAccepted = isDesignerUser && designer?.status === "approved";
          const hasPmAccepted = isPmUser && manager?.status === "approved";
          const hasOwnerAccepted = isOwnerUser && owner?.status === "approved";

          if (hasDesignerAccepted || hasPmAccepted || hasOwnerAccepted) {
            return (
              <div className="flex items-center gap-2">
                {ViewButton}
                <CheckCircle2 size={20} className="text-emerald-500" title="Accepted" />
              </div>
            );
          }

          if (isPmUser && designer?.status === "pending") {
            return (
              <div className="flex items-center gap-2">
                {ViewButton}
                <span className="text-xs text-slate-400 font-medium italic">
                  Pending Designer Approval
                </span>
              </div>
            );
          }

          if (isOwnerUser && owner?.status === "pending" && !showOwner) {
            return (
              <div className="flex items-center gap-2">
                {ViewButton}
                <span className="text-xs text-slate-400 font-medium italic">
                  Pending Previous Approvals
                </span>
              </div>
            );
          }

          const activeRole = showDesigner ? "Designer" : showManager ? "Project Manager" : showOwner ? "Owner" : null;

          return (
            <div className="flex items-center gap-2">
              {ViewButton}
              {activeRole && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRoleSelect(activeRole, l, 'approve');
                    }}
                    className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                    title={`Approve as ${activeRole}`}
                  >
                    <Check size={18} strokeWidth={2.5} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRoleSelect(activeRole, l, 'reject');
                    }}
                    className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                    title={`Reject as ${activeRole}`}
                  >
                    <X size={18} strokeWidth={2.5} />
                  </button>
                </>
              )}
            </div>
          );
        },
      }
    );
    return cols;
  }, [user, teamMembers, isViewOnly, leads, localSearch, localFilters]);

  return (
    <div className="flex flex-col gap-4">
      <InitialQuotationApprovalControlPanel
        search={localSearch}
        setSearch={setLocalSearch}
        filters={localFilters}
        setFilters={setLocalFilters}
        onExport={handleExport}
        isExporting={isExporting}
      />

      <div className="w-full h-full bg-white rounded-xl shadow-sm border border-slate-200/60 overflow-hidden">
        <DataTable
          columns={columns}
          data={filteredLeads}
          isLoading={isLoading}
          className="border-none shadow-none rounded-none"
        />

        <ConfirmationModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirmRole}
          role={selectedRole}
          loading={actionLoading}
          actionType={actionType}
        />

        <LeadDetailsViewModal
          isOpen={!!viewModalLead}
          onClose={() => setViewModalLead(null)}
          lead={viewModalLead}
        />
      </div>
    </div>
  );
};

export default RoleSelectionScreen;
